/**
 * The single way this app talks to the API.
 *
 * `credentials: 'include'` on every call, without exception — the session lives in a cookie on a
 * different origin, and a fetch that forgets this fails in a way that looks like being signed out.
 */
export class ApiError extends Error {
  readonly status: number;
  /** `MFA_REQUIRED`, `FORBIDDEN`, better-auth's own codes, or `UNKNOWN`. */
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

interface ErrorBody {
  error?: string;
  code?: string;
  message?: string;
}

/**
 * WHAT HAPPENS WHEN A SESSION DIES WHILE SHE IS USING THE APP.
 *
 * Sessions last thirty days and slide, so this is rare — and rare is exactly why it was broken. On
 * a cold load the root already shows the sign-in screen when `/api/me` refuses. The hole is the
 * session that expires, or is revoked, while the app is ALREADY OPEN: React Query still holds the
 * actor, so the shell keeps rendering her name and her navigation while every page behind it is
 * refused. `AccountSettings` renders nothing without an account, so what she sees is a blank page
 * beside a working menu, and nothing anywhere says she has been signed out.
 *
 * One listener rather than a check in every screen. A rule that each page has to remember is a rule
 * that the next page forgets, and this one fails silently when forgotten.
 *
 * ⚠ SIGNING IN IS EXCLUDED. A wrong password is a 401 too, and treating it as an expired session
 * would clear the form underneath somebody who simply mistyped.
 */
type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler | null = null;

export function handleUnauthorized(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

/**
 * ⚠ THE SESSION PROBE ITSELF MUST NEVER TRIGGER THIS, AND LEAVING IT OUT COST 3,000 REQUESTS.
 *
 * The handler's job is to re-ask `/api/me`. If a refused `/api/me` also fired the handler, the
 * answer to being signed out was to ask again — refused, ask again — until the API's own rate limit
 * stopped it at 429. The browser did that in a couple of seconds and the page never changed.
 *
 * So the probe is excluded, which breaks the loop at its source: something else's 401 asks `me`
 * once, `me` answers refused, and the root renders the sign-in screen.
 *
 * `/api/auth` and `/api/sign-in` are excluded for a different reason: a wrong password is a 401 too,
 * and treating it as an expired session would clear the form under somebody who simply mistyped.
 */
function neverMeansExpired(path: string): boolean {
  return path === '/api/me' || path.startsWith('/api/auth') || path.startsWith('/api/sign-in');
}

/**
 * NO CONNECTION IS AN ApiError TOO (2026-10-19). `fetch` throws a bare TypeError when the request
 * never reaches anybody, and a dozen screens show an error only when it is an ApiError — so a save
 * on a phone that had lost its signal failed with nothing on the screen at all. Status 0, because
 * no server answered.
 */
export const NETWORK_ERROR_MESSAGE = "Couldn't connect. Check your connection and try again.";

export function isNetworkError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'NETWORK';
}

/**
 * THE SCREEN'S OWN MESSAGE, UNLESS THE CONNECTION WAS THE PROBLEM (2026-10-20). Sign-in answered
 * every failure with "That email and password do not match", so a phone with no signal sent her off
 * to reset a password that was fine. Only a server's answer is about what she typed.
 */
export function unlessOffline(error: unknown, message: string): string {
  return isNetworkError(error) ? NETWORK_ERROR_MESSAGE : message;
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  let text: string;
  try {
    response = await fetch(path, {
      ...init,
      credentials: 'include',
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
    text = await response.text();
  } catch (error) {
    /* A cancelled request is not a failure: React Query cancels on purpose. */
    if (init?.signal?.aborted) throw error;
    throw new ApiError(0, 'NETWORK', NETWORK_ERROR_MESSAGE);
  }
  /*
    A proxy's error page is HTML, not JSON — a 502 on a weak connection used to surface as
    "Unexpected token '<'" on the score sheet. Anything unreadable is simply no body.
  */
  let body: unknown = {};
  let readable = text.length > 0;
  try {
    body = text.length > 0 ? JSON.parse(text) : {};
  } catch {
    readable = false;
    if (response.ok) throw new ApiError(response.status, 'BAD_RESPONSE', 'Something went wrong. Try again.');
  }

  if (!response.ok) {
    /*
      A 403 asks too (2026-10-18). Sign-in is per BROWSER, not per tab: signing in as somebody else
      in another tab left this one drawing the first person's screens, and every save it made was
      refused. Re-asking who is signed in lets the root notice the change and redraw for them.
    */
    if ((response.status === 401 || response.status === 403) && !neverMeansExpired(path)) onUnauthorized?.();

    const shape = body as ErrorBody;
    throw new ApiError(
      response.status,
      /* NO_BODY: nothing of ours came back — a proxy gave up, so the connection is the likely cause. */
      readable ? shape.error ?? shape.code ?? 'UNKNOWN' : 'NO_BODY',
      shape.message ?? 'Something went wrong.',
    );
  }

  return body as T;
}

export function apiPost<T>(path: string, payload: unknown): Promise<T> {
  return apiFetch<T>(path, { method: 'POST', body: JSON.stringify(payload) });
}

export function apiDelete(path: string): Promise<void> {
  return apiFetch<void>(path, { method: 'DELETE' });
}

/** A PATCH says what CHANGED, not what everything is — send only the keys being edited. */
export function apiPatch<T>(path: string, payload: unknown): Promise<T> {
  return apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(payload) });
}

/**
 * A PUT says what the whole thing IS. Used where a screen holds every field at once and sends
 * them together, so a value cleared on the form is a value cleared on the server rather than one
 * PATCH quietly left alone.
 */
export function apiPut<T>(path: string, payload: unknown): Promise<T> {
  return apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(payload) });
}
